import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { LDFlags } from '../../config';
import featureConfig from '../../config/featureConfig';
import { getFriendlyError } from '../../utils/error';
import { getDemoCurrentWeekEnergyBudget, getDemoWeekEnergyBudget } from './mocks/demoData';
import { WeeklyEnergyBudget, WeeklyEnergyBudgetsParams } from './types';
import { getCurrentWeekEnergyBudget, getWeeklyEnergyBudgets } from './weekly-energy-budgets';

export const QUERY_KEY_CURRENT_WEEK_ENERGY_BUDGET = 'current-week-energy-budget';
export const QUERY_KEY_WEEKLY_ENERGY_BUDGETS = 'weekly-energy-budgets';

interface UseCurrentWeekEnergyBudgetProps {
  data: WeeklyEnergyBudget | undefined;
  error: string | null;
  isLoading: boolean;
  isFetched: boolean;
}

export const useCurrentWeekEnergyBudget = (
  propertyId: string,
  fetchEnabled = true,
): UseCurrentWeekEnergyBudgetProps => {
  const flags = useFlags<LDFlags>();
  const isDemoMode = featureConfig.demoMode(undefined, flags);
  const isDemoQueryEnabled = isDemoMode && fetchEnabled && Boolean(propertyId);
  const demoBudget = useMemo(
    () => (isDemoQueryEnabled ? getDemoCurrentWeekEnergyBudget() : undefined),
    [isDemoQueryEnabled],
  );

  const { data, error, isLoading, isFetched } = useQuery<WeeklyEnergyBudget | undefined, AxiosError>(
    [QUERY_KEY_CURRENT_WEEK_ENERGY_BUDGET, propertyId],
    () => getCurrentWeekEnergyBudget(propertyId),
    {
      enabled: fetchEnabled && Boolean(propertyId) && !isDemoMode,
    },
  );

  if (isDemoMode) {
    return { data: demoBudget, error: null, isLoading: false, isFetched: true };
  }

  return {
    data,
    error: getFriendlyError(error, 'current week energy budget') || null,
    isLoading,
    isFetched,
  };
};

interface UseWeeklyEnergyBudgetsProps {
  data: WeeklyEnergyBudget | undefined;
  error: string | null;
  isLoading: boolean;
  isFetched: boolean;
  errorStatusCode?: number;
}

export const useWeeklyEnergyBudgets = (
  propertyId: string,
  params?: WeeklyEnergyBudgetsParams,
  fetchEnabled = true,
): UseWeeklyEnergyBudgetsProps => {
  const flags = useFlags<LDFlags>();
  const isDemoMode = featureConfig.demoMode(undefined, flags);
  const isDemoQueryEnabled = isDemoMode && fetchEnabled && Boolean(propertyId);
  const demoBudget = useMemo(
    () => (isDemoQueryEnabled ? getDemoWeekEnergyBudget(params?.startDate) : undefined),
    [isDemoQueryEnabled, params?.startDate],
  );

  const { data, error, isLoading, isFetched } = useQuery<WeeklyEnergyBudget | undefined, AxiosError>(
    [QUERY_KEY_WEEKLY_ENERGY_BUDGETS, propertyId, params],
    () => getWeeklyEnergyBudgets(propertyId, params),
    {
      enabled: fetchEnabled && Boolean(propertyId) && !isDemoMode,
    },
  );

  if (isDemoMode) {
    return { data: demoBudget, error: null, isLoading: false, isFetched: true };
  }

  return {
    data,
    error: getFriendlyError(error, 'weekly energy budgets') || null,
    isLoading,
    isFetched,
    errorStatusCode: error?.status,
  };
};
